import { CommonModule } from '@angular/common';
import {
	Inject,
	inject,
	InjectionToken,
	Injector,
	NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { DcButtonModule } from '@datachain/ui-sdk/components/app-button';
import { DcChipModule } from '@datachain/ui-sdk/components/app-chip';
import {
	AppModalComponent,
	DcAppModalModule,
} from '@datachain/ui-sdk/components/app-modal';
import { DcOverlayBadgeModule } from '@datachain/ui-sdk/components/app-overlay-badge';
import { DcPageModule } from '@datachain/ui-sdk/components/app-page';
import { DcPanelModule } from '@datachain/ui-sdk/components/app-panel';
import { DcPopoverModule } from '@datachain/ui-sdk/components/app-popover';
import {
	AppTagConfiguratorComponent,
	DcTagConfiguratorModule,
} from '@datachain/ui-sdk/components/app-tag-configurator';
import {
	AppTagEditComponent,
	DcTagEditModule,
} from '@datachain/ui-sdk/components/app-tag-edit';
import { DcToggleButtonsModule } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { DcAvatarModule } from '@datachain/ui-sdk/components/avatar';
import {
	AppIcons,
	DcCoreIconsInjectable,
} from '@datachain/ui-sdk/components/dc-icon';
import { DcNavigateDirectiveModule } from '@datachain/ui-sdk/directives';
import {
	DcArrayPipeModule,
	DcDayjsDatePipeModule,
	DcTruncatePipeModule,
} from '@datachain/ui-sdk/pipes';
import { DxDataGridModule } from 'devextreme-angular';
import { NgxColorsModule } from 'ngx-colors';

import { NgCore } from '../core/ng-core';
import { AppActionsIcons } from './app-actions.icons';
import { AppUiComponentIcons } from './app-components.icons';
import { AppDcIcons } from './app-dc.icons';
import { AppGdIcons } from './app-gd.icons';
import { AppHdIcons } from './app-hd.icons';
import { AppCardOutlineComponent } from './components/app-card-outline/app-card-outline.component';
import { AppDcUserComponent } from './components/app-dc-user/app-dc-user.component';
import { AppDeleteReportComponent } from './components/app-delete-report/app-delete-report.component';
import { AppDocHintsComponent } from './components/app-doc-hints/app-doc-hints.component';
import { AppLoaderComponent } from './components/app-loader/app-loader.component';
import { AppMemberListComponent } from './components/app-member-list/app-member-list.component';
import { AppMetadataComponent } from './components/app-metadata/app-metadata.component';
import { AppPermissionsConfigComponent } from './components/app-permissions-configurator/app-permissions-config.component';
import { AppPublicProjectViewComponent } from './components/app-public-project-view/app-public-project-view.component';
import { AppNavDocComponent } from './components/nav-doc/nav-doc.component';
import { SplashComponent } from './components/splash/splash.component';
import { CopyOnHoverDirective } from './directives/copy-hover.directive';
import { DxtremeModule } from './dxtreme.module';
import { MaterialModule } from './material.module';
import { DayjsDurationFormatPipe } from './pipes/dayjs-duration-format.pipe';
import { FileSizePipe } from './pipes/file-size.pipe';
import { ToCompatibleTypeMappingPipe } from './pipes/to-compatible-type-mapping.pipe';
import { ToDayjsDurationPipe } from './pipes/to-dayjs-duration.pipe';

export const DcIconsInjectable = new InjectionToken('dc default namespace', {
	factory: (): AppIcons =>
		new AppDcIcons(inject(MatIconRegistry), inject(DomSanitizer)),
});

export const GdIconsInjectable = new InjectionToken('Generics data namespace', {
	factory: (): AppIcons =>
		new AppGdIcons(inject(MatIconRegistry), inject(DomSanitizer)),
});

export const HdIconsInjectable = new InjectionToken('Handle data namespace', {
	factory: (): AppIcons =>
		new AppHdIcons(inject(MatIconRegistry), inject(DomSanitizer)),
});

export const ActionIconsInjectable = new InjectionToken(
	'Actions data namespace',
	{
		factory: (): AppIcons =>
			new AppActionsIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	}
);

export const UiComponentIconsInjectable = new InjectionToken(
	'ADOBIS UI components data namespace',
	{
		factory: (): AppIcons =>
			new AppUiComponentIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	}
);

const availableComponents = [
	AppMetadataComponent,
	AppPermissionsConfigComponent,
	AppNavDocComponent,
	AppCardOutlineComponent,
	AppLoaderComponent,
	AppPublicProjectViewComponent,
	AppDeleteReportComponent,
	CopyOnHoverDirective,
	AppDcUserComponent,
	DayjsDurationFormatPipe,
	FileSizePipe,
	ToDayjsDurationPipe,
	ToCompatibleTypeMappingPipe,
	AppDocHintsComponent,
	AppMemberListComponent,
	SplashComponent,
];

@NgModule({
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MaterialModule,
		DxtremeModule,
		FormsModule,
		NgxColorsModule,
		DxDataGridModule,
		DcPageModule,
		DcPanelModule,
		DcArrayPipeModule,
		DcAvatarModule,
		DcButtonModule,
		DcOverlayBadgeModule,
		DcDayjsDatePipeModule,
		DcPopoverModule,
		DcNavigateDirectiveModule,
		DcAppModalModule,
		DcTagEditModule,
		DcTagConfiguratorModule,
		MatFormFieldModule,
		DcTruncatePipeModule,
		DcToggleButtonsModule,
		DcChipModule,
	],
	providers: [NgCore],
	declarations: availableComponents,
	exports: [
		...availableComponents,
		AppModalComponent,
		AppTagEditComponent,
		AppTagConfiguratorComponent,
	],
})
export class UiModule {
	public constructor(
		private readonly injector: Injector,
		@Inject(DcCoreIconsInjectable) private readonly coreIcons: AppIcons
	) {
		this.coreIcons.addIconsToRegistry();
	}
}
